import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import {
  AdminStore,
  AdminStoresState,
  StoreActionType,
  StoreApprovalStatus,
  VendorKyc,
} from "types/adminStore";

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

const extractList = (responseData: unknown): AdminStore[] => {
  if (Array.isArray(responseData)) {
    return responseData as AdminStore[];
  }

  if (!responseData || typeof responseData !== "object") {
    return [];
  }

  const data = responseData as {
    data?: unknown;
    stores?: unknown;
    results?: unknown;
  };

  if (Array.isArray(data.data)) {
    return data.data as AdminStore[];
  }

  if (Array.isArray(data.stores)) {
    return data.stores as AdminStore[];
  }

  if (Array.isArray(data.results)) {
    return data.results as AdminStore[];
  }

  if (data.data && typeof data.data === "object") {
    const nested = data.data as { stores?: unknown; results?: unknown };
    if (Array.isArray(nested.stores)) {
      return nested.stores as AdminStore[];
    }
    if (Array.isArray(nested.results)) {
      return nested.results as AdminStore[];
    }
  }

  return [];
};

const extractItem = (responseData: unknown): AdminStore | null => {
  if (!responseData || typeof responseData !== "object") {
    return null;
  }

  const data = responseData as { data?: unknown };
  const candidate = data.data || responseData;

  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as AdminStore;
  }

  return null;
};

const extractKyc = (responseData: unknown): VendorKyc | null => {
  if (!responseData || typeof responseData !== "object") {
    return null;
  }

  const data = responseData as { data?: unknown };
  const candidate = data.data || responseData;

  if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
    return candidate as VendorKyc;
  }

  return null;
};

export const fetchStoresByApprovalStatus = createAsyncThunk<
  { status: StoreApprovalStatus; stores: AdminStore[] },
  StoreApprovalStatus,
  { rejectValue: string }
>("adminStores/fetchStoresByApprovalStatus", async (status, { rejectWithValue }) => {
  if (!status) {
    return rejectWithValue("Approval status is required.");
  }

  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.stores.listByApprovalStatus(status)
    );

    return {
      status,
      stores: extractList(response.data),
    };
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const fetchStoresByMarket = createAsyncThunk<
  AdminStore[],
  number | string,
  { rejectValue: string }
>("adminStores/fetchStoresByMarket", async (marketId, { rejectWithValue }) => {
  if (!marketId) {
    return rejectWithValue("Market id is missing.");
  }

  try {
    const response = await axiosInstance.get(
      API_ENDPOINTS.stores.byMarket(marketId, "approved")
    );

    return extractList(response.data);
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const fetchStoreById = createAsyncThunk<
  AdminStore,
  number,
  { rejectValue: string }
>("adminStores/fetchStoreById", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(API_ENDPOINTS.stores.detail(id));
    const store = extractItem(response.data);

    if (!store) {
      return rejectWithValue("Unable to fetch store.");
    }

    return store;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const fetchVendorKyc = createAsyncThunk<
  VendorKyc | null,
  number,
  { rejectValue: string }
>("adminStores/fetchVendorKyc", async (vendorId, { rejectWithValue }) => {
  if (!vendorId) {
    return rejectWithValue("Vendor id is missing. Cannot load KYC.");
  }

  try {
    const response = await axiosInstance.get(API_ENDPOINTS.kyc.byUser(vendorId));
    return extractKyc(response.data);
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const approveStore = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("adminStores/approveStore", async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.patch(API_ENDPOINTS.stores.approve(id));
    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const declineStore = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("adminStores/declineStore", async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.patch(API_ENDPOINTS.stores.decline(id));
    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: AdminStoresState = {
  stores: [],
  storesByApprovalStatus: {},
  marketStores: [],
  selectedStore: null,
  selectedStoreKyc: null,
  loading: false,
  marketStoresLoading: false,
  detailLoading: false,
  kycLoading: false,
  actionLoading: false,
  actionStoreId: null,
  actionType: null,
  error: null,
  kycError: null,
};

const removeStoreFromLists = (state: AdminStoresState, id: number) => {
  state.stores = state.stores.filter((store) => store.id !== id);
  state.marketStores = state.marketStores.filter((store) => store.id !== id);
  Object.keys(state.storesByApprovalStatus).forEach((status) => {
    state.storesByApprovalStatus[status] = state.storesByApprovalStatus[
      status
    ].filter((store) => store.id !== id);
  });
};

const setActionPending = (
  state: AdminStoresState,
  id: number,
  actionType: StoreActionType
) => {
  state.actionLoading = true;
  state.actionStoreId = id;
  state.actionType = actionType;
  state.error = null;
};

const clearActionPending = (state: AdminStoresState) => {
  state.actionLoading = false;
  state.actionStoreId = null;
  state.actionType = null;
};

const adminStoreSlice = createSlice({
  name: "adminStores",
  initialState,
  reducers: {
    clearAdminStoreError: (state) => {
      state.error = null;
    },
    setSelectedStore: (state, action: PayloadAction<AdminStore | null>) => {
      state.selectedStore = action.payload;
    },
    clearSelectedStore: (state) => {
      state.selectedStore = null;
    },
    clearSelectedStoreKyc: (state) => {
      state.selectedStoreKyc = null;
      state.kycLoading = false;
      state.kycError = null;
    },
    clearMarketStores: (state) => {
      state.marketStores = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStoresByApprovalStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoresByApprovalStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.storesByApprovalStatus[action.payload.status] =
          action.payload.stores;
        state.stores = action.payload.stores;
      })
      .addCase(fetchStoresByApprovalStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch stores.";
      })
      .addCase(fetchStoresByMarket.pending, (state) => {
        state.marketStoresLoading = true;
        state.error = null;
      })
      .addCase(fetchStoresByMarket.fulfilled, (state, action) => {
        state.marketStoresLoading = false;
        state.marketStores = action.payload;
      })
      .addCase(fetchStoresByMarket.rejected, (state, action) => {
        state.marketStoresLoading = false;
        state.error = action.payload || "Unable to fetch market stores.";
      })
      .addCase(fetchStoreById.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedStore = action.payload;
      })
      .addCase(fetchStoreById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || "Unable to fetch store.";
      })
      .addCase(fetchVendorKyc.pending, (state) => {
        state.kycLoading = true;
        state.kycError = null;
        state.selectedStoreKyc = null;
      })
      .addCase(fetchVendorKyc.fulfilled, (state, action) => {
        state.kycLoading = false;
        state.selectedStoreKyc = action.payload;
      })
      .addCase(fetchVendorKyc.rejected, (state, action) => {
        state.kycLoading = false;
        state.kycError = action.payload || "Unable to load KYC details.";
        state.selectedStoreKyc = null;
      })
      .addCase(approveStore.pending, (state, action) => {
        setActionPending(state, action.meta.arg, "approve");
      })
      .addCase(approveStore.fulfilled, (state, action) => {
        const id = action.payload;
        const store = state.storesByApprovalStatus.awaiting_approval?.find(
          (item) => item.id === id
        );

        removeStoreFromLists(state, id);

        if (store) {
          const approvedStore = {
            ...store,
            approval_status: "approved",
          };
          state.storesByApprovalStatus.approved = [
            ...(state.storesByApprovalStatus.approved || []),
            approvedStore,
          ];
        }

        clearActionPending(state);
      })
      .addCase(approveStore.rejected, (state, action) => {
        clearActionPending(state);
        state.error = action.payload || "Unable to approve store.";
      })
      .addCase(declineStore.pending, (state, action) => {
        setActionPending(state, action.meta.arg, "decline");
      })
      .addCase(declineStore.fulfilled, (state, action) => {
        removeStoreFromLists(state, action.payload);
        clearActionPending(state);
      })
      .addCase(declineStore.rejected, (state, action) => {
        clearActionPending(state);
        state.error = action.payload || "Unable to decline store.";
      });
  },
});

export const {
  clearAdminStoreError,
  clearMarketStores,
  clearSelectedStore,
  clearSelectedStoreKyc,
  setSelectedStore,
} = adminStoreSlice.actions;
export default adminStoreSlice.reducer;
